import { useState } from "react";

import { DateTime } from "luxon";
import {
  ArrowPathIcon,
  CheckIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";

import useCustomerDetails from "../../data/useCustomerDetails";
import usePartsLookupMutations from "../../data/usePartsLookupMutations";
import { IPart } from "../../models/Part";
import { PartProBlackList } from "../../models/PartLookup";
import classNames from "../../utils/classNames";
import useNotification from "../notifications/useNotifications";
import ConfirmModal from "./Confirm/ConfirmModal";
import Tooltip from "./Tooltip";

// Compact sibling of the PartPro price tile in UnlistedOfferPrice - same
// mutation and same "this costs a token" confirmation, sized to sit inline
// next to a part number input.
export default function PartProLookupButton({
  part,
  className,
}: {
  part: IPart;
  className?: string;
}) {
  const { lookupMutation } = usePartsLookupMutations(part.id);
  const { brand } = useCustomerDetails();
  const { addNotification } = useNotification();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const isBlacklisted = PartProBlackList.includes(
    part.vehicle?.combinedMake?.toLowerCase() ?? ""
  );
  const ucid = (part.category?.ucid ?? "").trim();

  if (!ucid || isBlacklisted) return null;

  const unlocked =
    part.partsLookupRequestId != null || part.lastPartLookupChecked != null;

  const handleLookup = () => {
    if (unlocked) {
      setConfirmOpen(false);
      return;
    }

    setLoading(true);
    lookupMutation.mutate(undefined, {
      onSuccess: (data) => {
        setLoading(false);

        if (!data.data.requestedParts || data.data.requestedParts.length === 0) {
          addNotification({ primaryText: "No Parts Found", variant: "info" });
          return;
        }

        const categoryId = part.category?.id ?? part.categoryId;
        part.suggestedPrice =
          data.data.requestedParts.find((p) => p.partCategory?.id === categoryId)
            ?.priceInformation.avgPrice || 0;
        part.lastPartLookupChecked = DateTime.now().toISO();

        addNotification({
          primaryText: "PartPro Lookup Successful",
          variant: "success",
        });
      },
      onError: (error) => {
        console.error("PartPro lookup failed:", error);
        setLoading(false);
        addNotification({
          primaryText: "PartPro Lookup Failed",
          variant: "error",
        });
      },
    });

    setConfirmOpen(false);
  };

  return (
    <>
      <ConfirmModal
        open={confirmOpen}
        setOpen={setConfirmOpen}
        onConfirm={handleLookup}
        isLoading={loading}
        title={"Activate PartPro Lookup?"}
        message={`This will start a PartPro lookup on this vehicle, this may take some time.\n
                  This will check against an exact part number match, display it for you in the table, show how many it matched on and give you an accurate price estimate for the parts.\n
                  This costs 1 token per lookup per vehicle (multiple parts from the same vehicle count as one lookup), you have ${
                    brand.data?.data.lookupTokens ?? 0
                  } tokens remaining.`}
        confirmButtonText={null}
      />

      <Tooltip
        content={
          unlocked
            ? "PartPro lookup already run for this vehicle"
            : "Run a PartPro lookup (costs 1 token per vehicle)"
        }
        className={classNames("flex-none", className)}
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (!unlocked && !loading) setConfirmOpen(true);
          }}
          disabled={unlocked || loading}
          aria-label={
            unlocked ? "PartPro lookup already run" : "Run a PartPro lookup"
          }
          className={classNames(
            "flex-none rounded p-0.5 transition-colors",
            unlocked
              ? "cursor-default text-emerald-600"
              : "text-wilma-600 hover:bg-wilma-50",
            loading && "animate-pulse"
          )}
        >
          {loading ? (
            <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : unlocked ? (
            <CheckIcon className="h-4 w-4" aria-hidden="true" />
          ) : (
            <LockClosedIcon className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
      </Tooltip>
    </>
  );
}
